import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {

  private currentTheme: 'system' | 'dark' | 'light' = 'system';

  constructor() {
    this.loadTheme();
  }

  loadTheme() {
    const savedTheme = localStorage.getItem('theme') as 'system' | 'dark' | 'light';
    this.currentTheme = savedTheme || 'system';
    this.applyTheme(this.currentTheme);
  }

  public getThemes(){
    return this.currentTheme;
  }

  setTheme(theme: 'system' | 'light' | 'dark') {
    this.currentTheme = theme;
    localStorage.setItem('theme', theme);
    this.applyTheme(theme);
  
    // განვაახლოთ theme_color და background_color
    this.updateMetaThemeColor(theme);
    this.updateManifestThemeColor(theme);
  }

  applyTheme(theme: 'system' | 'dark' | 'light') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
    
    if (theme === 'system') {
      const isDark = prefersDark.matches;
      this.toggleTheme(isDark ? 'dark' : 'light');
      this.updateFavicon(isDark ? 'dark' : 'light');
      this.updateThemeColor(isDark ? '#000000' : '#fafafa');
      
      prefersDark.addEventListener('change', (event) => {
        const newTheme = event.matches ? 'dark' : 'light';
        this.toggleTheme(newTheme);
        this.updateFavicon(newTheme);
        this.updateThemeColor(newTheme === 'dark' ? '#000000' : '#fafafa');
      });
    } else {
      this.toggleTheme(theme);
      this.updateFavicon(theme);
      this.updateThemeColor(theme === 'dark' ? '#000000' : '#fafafa');
    }
  }

  // ThemeService-ში შეიძლება გამოვიყენოთ შემდეგი ფუნქცია:
  updateManifestThemeColor(theme: 'system' | 'dark' | 'light') {
    const manifest = document.querySelector('link[rel="manifest"]') as HTMLLinkElement;
  
    if (manifest) {
      fetch(manifest.href)
        .then(response => response.json())
        .then((data: any) => {
          // theme_color და background_color ცვლილება
          data.theme_color = theme === 'dark' ? '#2b2b2b' : '#fafafa';
          data.background_color = theme === 'dark' ? '#2b2b2b' : '#fafafa';
  
          // აიკონების განახლება
          data.icons = data.icons.map((icon: any) => {
            if (theme === 'dark') {
              icon.src = icon.src.replace('-light', '-dark');
            } else {
              icon.src = icon.src.replace('-dark', '-light');
            }
            return icon;
          });
  
          // მიამაგრე ახალი manifest. ფაილი (მაგალითად, ატვირთეთ სერვერზე)
          // აქ ფიქსაცია კი ხდება დროულად.
          const newManifestBlob = new Blob([JSON.stringify(data)], { type: 'application/json' });
          const newManifestURL = URL.createObjectURL(newManifestBlob);
  
          // ახალი manifest-ის განახლება
          manifest.href = newManifestURL;
        })
        .catch(err => {
          console.error('Error loading manifest:', err);
        });
    }
  }

  updateMetaThemeColor(theme: 'system' | 'dark' | 'light') {
    const themeMeta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement;
    if (themeMeta) {
      themeMeta.content = theme === 'dark' ? '#2b2b2b' : '#fafafa';
    }
  
    const backgroundMeta = document.querySelector('meta[name="background-color"]') as HTMLMetaElement;
    if (backgroundMeta) {
      backgroundMeta.content = theme === 'dark' ? '#2b2b2b' : '#fafafa';
    }
  }
  

  updateThemeColor(color: string) {
    const themeMeta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement;
    if (themeMeta) {
      themeMeta.content = color;
    }
  }

  toggleTheme(theme: 'system' | 'dark' | 'light') {
    if (theme === 'dark') {
      document.body.classList.add('dark-theme');
      document.body.classList.remove('light-theme');
    } else {
      document.body.classList.add('light-theme');
      document.body.classList.remove('dark-theme');
    }
  }

  updateFavicon(theme: 'system' | 'dark' | 'light') {
    const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    if (favicon) {
      const timestamp = new Date().getTime();
      const newFaviconUrl = theme === 'dark' ? 
        `https://s3.eu-north-1.amazonaws.com/menuload.com/app/logo-dark.png?v=${timestamp}` : 
        `https://s3.eu-north-1.amazonaws.com/menuload.com/app/logo.webp?v=${timestamp}`;
        
      console.log('Updating favicon to:', newFaviconUrl);
      favicon.href = newFaviconUrl;
    }
  }

  updateAppleTouchIcon(theme: 'system' | 'dark' | 'light') {
    const appleTouchIcons = document.querySelectorAll('link[rel="apple-touch-icon"]') as NodeListOf<HTMLLinkElement>;
  
    if (appleTouchIcons.length) {
      appleTouchIcons.forEach((icon: HTMLLinkElement) => {
        // აიკონის URL-ები შეცვლა
        if (theme === 'dark') {
          icon.href = icon.href.replace('-light', '-dark');
        } else if (theme === 'light') {
          icon.href = icon.href.replace('-dark', '-light');
        }
      });
    }
  }

}
